import { produce } from "immer";
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { validateFile } from "@/utils/fileValidation";
import { Flex, Text, ScrollArea, Container, TextField } from "@radix-ui/themes";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

import { Tabs } from "@/v2/components/tabs/Tabs";

import { MinimalUpload } from "./MinimalUpload";
import { KnowledgeFileUploadComponent } from "@/components/shared/upload-file";
import { params } from "@/params";

import styles from "./Drawer.module.scss";
import {
  defaultKnowledgeHubConfig,
  KnowledgeHubConfig,
  GOOGLE_FONTS,
  COLOR_FIELDS,
  CONTRAST_PAIRS,
} from "../core/pages/knowledge-hub/index.types";
import { coverPhotoChoices } from "../../index.types";
import { useUploadKnowledgeHubAssest } from "../../api";
import { ColorPicker } from "../core/components/ColorPicker";
import Select from "@/components/shared/select/Select";
import Switch from "@/components/shared/inputs/switch";

const ColorSection = ({
  mode,
  colors,
  onChange,
}: {
  mode: "dark" | "light";
  colors: KnowledgeHubConfig["theme"]["light"];
  onChange: (key: string, color: string) => void;
}) => (
  <Flex direction="column" gap="3">
    <Text size="2" weight="bold">
      {mode === "dark" ? "Dark" : "Light"} Mode Colors
    </Text>
    <div className={styles.colorGrid}>
      {COLOR_FIELDS.map((field) => {
        const backgroundFieldKey = CONTRAST_PAIRS[field.key];
        const backgroundColorToCheckAgainst = backgroundFieldKey
          ? colors[backgroundFieldKey as keyof typeof colors]
          : undefined;

        return (
          <div key={field.key} className={styles.colorItem}>
            <Text size="1">{field.label}</Text>
            <ColorPicker
              value={colors[field.key as keyof typeof colors]}
              onValueChange={(c) => onChange(field.key, c)}
              contrastWith={backgroundColorToCheckAgainst}
              hideContrastRatio={!backgroundColorToCheckAgainst}
            >
              <div
                style={{
                  width: "20px",
                  height: "20px",
                  borderRadius: "4px",
                  border: "1px solid var(--gray-a5)",
                  backgroundColor: colors[field.key as keyof typeof colors],
                  cursor: "pointer",
                }}
              />
            </ColorPicker>
          </div>
        );
      })}
    </div>
  </Flex>
);

export const KnowledgeHubDrawer = ({
  config,
  updateConfiguration,
  publishActionRef,
}: {
  config: KnowledgeHubConfig;
  updateConfiguration: (configuration: KnowledgeHubConfig) => void;
  publishActionRef: MutableRefObject<(() => boolean | void) | null>;
}) => {
  const [localConfig, setLocalConfig] = useState<KnowledgeHubConfig>(config);
  const themeConfig = localConfig.theme || defaultKnowledgeHubConfig.theme;
  const { mutateAsync: uploadAssest } = useUploadKnowledgeHubAssest();

  const localConfigRef = useRef(localConfig);

  const handleSave = useCallback((): boolean => {
    const currentConfig = localConfigRef.current;

    updateConfiguration(currentConfig);
    return true;
  }, [updateConfiguration]);

  useLayoutEffect(() => {
    publishActionRef.current = handleSave;

    return () => {
      publishActionRef.current = null;
    };
  }, [handleSave, publishActionRef]);

  const handleUploadAsset = useCallback(
    async (file: File, accept?: string[]) => {
      validateFile(file, {
        accept,
        maxSizeInBytes: 1024 * 1024,
      });

      const response = await uploadAssest(file);
      return {
        path: response.path,
        name: file.name,
      };
    },
    [uploadAssest]
  );

  const handleThemeColorChange = useCallback(
    (mode: "dark" | "light", key: string, value: string) => {
      setLocalConfig(
        produce((draft) => {
          if (!draft.theme) draft.theme = defaultKnowledgeHubConfig.theme;
          draft.theme[mode][key as keyof typeof draft.theme.light] = value;
        })
      );
    },
    [setLocalConfig]
  );

  const handleFontChange = useCallback(
    (fontFamily: string) => {
      const selectedFont = GOOGLE_FONTS.find((f) => f.value === fontFamily);
      if (selectedFont) {
        setLocalConfig(
          produce((draft) => {
            draft.font = {
              family: selectedFont.value,
              url: selectedFont.url,
            };
          })
        );
      }
    },
    [setLocalConfig]
  );

  useEffect(() => {
    localConfigRef.current = localConfig;
  }, [localConfig]);

  useEffect(() => {
    setLocalConfig(config);
    localConfigRef.current = config;
  }, [config]);

  return (
    <ScrollArea>
      <div className={styles.drawerContainer}>
        <Flex gap="1" align="center" justify="between">
          <Text
            size="8"
            weight="medium"
            style={{ fontFamily: "Instrument Serif" }}
          >
            Appearance
          </Text>
        </Flex>

        <Accordion type="multiple" defaultValue={["general"]}>
          <AccordionItem value="general">
            <AccordionTrigger>General</AccordionTrigger>
            <AccordionContent>
              <div className={styles.section}>
                <Flex direction="column" gap="1">
                  <Text className={styles.label}>Page Title</Text>
                  <TextField.Root
                    placeholder="Hello (User Name)"
                    value={localConfig.pageTitle || ""}
                    onChange={(e) =>
                      setLocalConfig(
                        produce((draft) => {
                          draft.pageTitle = e.target.value;
                        })
                      )
                    }
                    variant="soft"
                    color="gray"
                  />
                </Flex>

                <Flex direction="column" gap="1">
                  <Text className={styles.label}>Greeting Title</Text>
                  <TextField.Root
                    placeholder="How can I help you today?"
                    value={localConfig.greetingTitle || ""}
                    onChange={(e) =>
                      setLocalConfig(
                        produce((draft) => {
                          draft.greetingTitle = e.target.value;
                        })
                      )
                    }
                    variant="soft"
                    color="gray"
                  />
                </Flex>

                <Flex direction="column" gap="1">
                  <Text className={styles.label}>Input Place Holder</Text>
                  <TextField.Root
                    placeholder="Ask me anything..."
                    value={localConfig.inputPlaceholder || ""}
                    onChange={(e) =>
                      setLocalConfig(
                        produce((draft) => {
                          draft.inputPlaceholder = e.target.value;
                        })
                      )
                    }
                    variant="soft"
                    color="gray"
                  />
                </Flex>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="theme">
            <AccordionTrigger>Theme</AccordionTrigger>
            <AccordionContent>
              <div className={styles.section}>
                <Tabs
                  fullWidth
                  variant="data"
                  value={themeConfig.mode}
                  onValueChange={(value) =>
                    setLocalConfig(
                      produce((draft) => {
                        if (!draft.theme)
                          draft.theme = defaultKnowledgeHubConfig.theme;
                        draft.theme.mode = value as "light" | "dark" | "system";
                      })
                    )
                  }
                  tabs={[
                    { value: "light", label: "Light" },
                    { value: "dark", label: "Dark" },
                    { value: "system", label: "System" },
                  ]}
                />
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="branding">
            <AccordionTrigger>Branding</AccordionTrigger>
            <AccordionContent>
              <div className={styles.section}>
                <MinimalUpload
                  label="Logo"
                  enabled={localConfig.logoEnabled}
                  onEnabledChange={(enabled) =>
                    setLocalConfig(
                      produce((draft) => {
                        draft.logoEnabled = enabled;
                      })
                    )
                  }
                  fileSrc={localConfig.logoImageUrl}
                  onFileUpload={(file) =>
                    handleUploadAsset(file, [
                      "image/jpg",
                      "image/jpeg",
                      "image/png",
                      "image/webp",
                    ])
                  }
                  onSuccess={(filePath) => {
                    setLocalConfig(
                      produce((draft) => {
                        draft.logoImageUrl = filePath
                          ? `${params.KNOWLEDGE_HUB_CONFIG_URL}/${filePath}`
                          : undefined;
                      })
                    );
                  }}
                />

                <MinimalUpload
                  label="Favicon"
                  enabled={localConfig.faviconEnabled}
                  onEnabledChange={(enabled) =>
                    setLocalConfig(
                      produce((draft) => {
                        draft.faviconEnabled = enabled;
                      })
                    )
                  }
                  fileSrc={localConfig.faviconUrl}
                  onFileUpload={(file) =>
                    handleUploadAsset(file, [
                      "image/jpg",
                      "image/jpeg",
                      "image/png",
                      "image/webp",
                    ])
                  }
                  onSuccess={(filePath) => {
                    setLocalConfig(
                      produce((draft) => {
                        draft.faviconUrl = filePath
                          ? `${params.KNOWLEDGE_HUB_CONFIG_URL}/${filePath}`
                          : undefined;
                      })
                    );
                  }}
                />
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="background">
            <AccordionTrigger>Background</AccordionTrigger>
            <AccordionContent>
              <div className={styles.section}>
                <Flex direction="column" gap="4" mt="3" mr="1">
                  <Flex direction="column" gap="2">
                    <Flex justify="between" align="center">
                      <Text size="2">Light Mode Cover</Text>
                      <Switch
                        value={localConfig.coverPhoto?.light?.enabled}
                        onChange={(checked) => {
                          setLocalConfig(
                            produce((draft) => {
                              if (!draft.coverPhoto) {
                                draft.coverPhoto = {
                                  light: {
                                    enabled: false,
                                    type: "quack",
                                  },
                                  dark: {
                                    enabled: false,
                                    type: "quack",
                                  },
                                };
                              }
                              if (!draft.coverPhoto.light) {
                                draft.coverPhoto.light = {
                                  enabled: false,
                                  type: "quack",
                                };
                              }
                              draft.coverPhoto.light.enabled = checked;
                            })
                          );
                        }}
                      />
                    </Flex>
                    {localConfig.coverPhoto?.light?.enabled && (
                      <KnowledgeFileUploadComponent
                        variant="options"
                        choices={coverPhotoChoices}
                        defaultFileName={localConfig.coverPhoto?.light?.imageUrl
                          ?.split("/")
                          .pop()}
                        onFileUpload={(file) =>
                          handleUploadAsset(file, [
                            "image/jpg",
                            "image/jpeg",
                            "image/png",
                            "image/webp",
                          ])
                        }
                        fileSrc={localConfig.coverPhoto?.light?.imageUrl}
                        accept={[
                          "image/jpg",
                          "image/jpeg",
                          "image/png",
                          "image/webp",
                        ]}
                        maxSizeInBytes={1024 * 1024}
                        onSuccess={(filePath, option) => {
                          setLocalConfig(
                            produce((draft) => {
                              if (!draft.coverPhoto) {
                                draft.coverPhoto = {
                                  light: {
                                    enabled: false,
                                    type: "quack",
                                  },
                                  dark: {
                                    enabled: false,
                                    type: "quack",
                                  },
                                };
                              }
                              if (!draft.coverPhoto.light) {
                                draft.coverPhoto.light = {
                                  enabled: false,
                                  type: "quack",
                                };
                              }
                              draft.coverPhoto.light.imageUrl = filePath
                                ? option
                                  ? filePath
                                  : `${params.KNOWLEDGE_HUB_CONFIG_URL}/${filePath}`
                                : undefined;
                            })
                          );
                        }}
                      />
                    )}
                  </Flex>

                  <Flex direction="column" gap="2">
                    <Flex justify="between" align="center">
                      <Text size="2">Dark Mode Cover</Text>
                      <Switch
                        value={localConfig.coverPhoto?.dark?.enabled}
                        onChange={(checked) => {
                          setLocalConfig(
                            produce((draft) => {
                              if (!draft.coverPhoto) {
                                draft.coverPhoto = {
                                  light: {
                                    enabled: false,
                                    type: "quack",
                                  },
                                  dark: {
                                    enabled: false,
                                    type: "quack",
                                  },
                                };
                              }
                              if (!draft.coverPhoto.dark) {
                                draft.coverPhoto.dark = {
                                  enabled: false,
                                  type: "quack",
                                };
                              }
                              draft.coverPhoto.dark.enabled = checked;
                            })
                          );
                        }}
                      />
                    </Flex>
                    {localConfig.coverPhoto?.dark?.enabled && (
                      <KnowledgeFileUploadComponent
                        variant="options"
                        choices={coverPhotoChoices}
                        defaultFileName={localConfig.coverPhoto?.dark?.imageUrl
                          ?.split("/")
                          .pop()}
                        onFileUpload={(file) =>
                          handleUploadAsset(file, [
                            "image/jpg",
                            "image/jpeg",
                            "image/png",
                            "image/webp",
                          ])
                        }
                        fileSrc={localConfig.coverPhoto?.dark?.imageUrl}
                        accept={[
                          "image/jpg",
                          "image/jpeg",
                          "image/png",
                          "image/webp",
                        ]}
                        maxSizeInBytes={1024 * 1024}
                        onSuccess={(filePath, option) => {
                          setLocalConfig(
                            produce((draft) => {
                              if (!draft.coverPhoto) {
                                draft.coverPhoto = {
                                  light: {
                                    enabled: false,
                                    type: "quack",
                                  },
                                  dark: {
                                    enabled: false,
                                    type: "quack",
                                  },
                                };
                              }
                              if (!draft.coverPhoto.dark) {
                                draft.coverPhoto.dark = {
                                  enabled: false,
                                  type: "quack",
                                };
                              }
                              draft.coverPhoto.dark.imageUrl = filePath
                                ? option
                                  ? filePath
                                  : `${params.KNOWLEDGE_HUB_CONFIG_URL}/${filePath}`
                                : undefined;
                            })
                          );
                        }}
                      />
                    )}
                  </Flex>
                </Flex>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="colors">
            <AccordionTrigger>Colors</AccordionTrigger>
            <AccordionContent>
              <div className={styles.section}>
                <ColorSection
                  mode="light"
                  colors={themeConfig.light}
                  onChange={(key, val) => handleThemeColorChange("light", key, val)}
                />
                <ColorSection
                  mode="dark"
                  colors={themeConfig.dark}
                  onChange={(key, val) => handleThemeColorChange("dark", key, val)}
                />
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="typography">
            <AccordionTrigger>Typography</AccordionTrigger>
            <AccordionContent>
              <div className={styles.section}>
                <Select
                  value={localConfig.font?.family || "Inter"}
                  onChange={handleFontChange}
                  options={GOOGLE_FONTS}
                />
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <Container className={styles.bottomSpacer} />
      </div>
    </ScrollArea>
  );
};
